/**
 * StaffPingAlert — Iter 387 · Feb 14 2026
 * ───────────────────────────────────────
 * Karine spec: "where is the ping tanya or ping karine button when we have
 * a client waiting and the ping should hear and sound and give the client's
 * name in a flash on the screen for either karine, tanya or other staff we hire"
 *
 * How it works:
 *   1. Subscribes to the existing `/api/checkin/admin/stream` SSE feed.
 *   2. Listens for `provider_ping` events broadcast by the backend when
 *      Marc (or any owner) taps the "Ping Provider" button on the
 *      LiveCheckinBanner card.
 *   3. If the ping is targeted at the currently-logged-in staffer
 *      (email match), it renders a full-screen amber overlay with the
 *      client's name in giant type AND plays a two-tone bell chord.
 *
 * The existing "Ping Provider" button in LiveCheckinBanner already POSTs
 * to /api/checkin/admin/ping-provider which now ALSO broadcasts this
 * event — so there is no separate "Ping Karine / Ping Tanya" button; the
 * generic Ping button on any waiting-room card routes the alert to
 * whichever provider is assigned to that appointment.
 */
import { useEffect, useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

/** Web-Audio "bell" — no external asset needed. Two-tone chord decays
 * over ~0.9s, plays at a reasonable indoor-volume ~-6dB. */
function playBellChord() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const now = ctx.currentTime;
    const notes = [880, 1174.66]; // A5 + D6 — clinic-friendly bright chime
    notes.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, now);
      gain.gain.linearRampToValueAtTime(0.35, now + 0.02 + idx * 0.06);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.9 + idx * 0.1);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + idx * 0.06);
      osc.stop(now + 1.2);
    });
    setTimeout(() => { try { ctx.close(); } catch { /* noop */ } }, 1800);
  } catch { /* audio blocked — no-op */ }
}

export default function StaffPingAlert() {
  const [pings, setPings] = useState([]);   // active on-screen alerts
  const meRef = useRef(null);
  const esRef = useRef(null);

  // Resolve who we are — needed to filter incoming pings
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/staff/me`, { credentials: "include" });
        if (r.ok) {
          const j = await r.json();
          meRef.current = { email: (j.email || "").toLowerCase(), name: j.name || "" };
        }
      } catch { /* not logged in — component simply stays silent */ }
    })();
  }, []);

  // Subscribe to SSE stream
  useEffect(() => {
    try {
      const es = new EventSource(`${API}/api/checkin/admin/stream`, { withCredentials: true });
      esRef.current = es;
      const handlePing = (e) => {
        try {
          const evt = JSON.parse(e.data);
          // Route pings that target this staffer
          const myEmail = (meRef.current?.email || "").toLowerCase();
          const targetEmail = (evt.provider_email || "").toLowerCase();
          // If we don't yet know who we are (staff/me still loading), or the
          // ping is for me, show it. Marc / front-desk stations receive
          // all pings so they can confirm delivery.
          if (myEmail && targetEmail && myEmail !== targetEmail) return;

          const id = `ping-${evt.checkin_id || Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          setPings((prev) => [...prev, {
            id,
            client_name: evt.client_name || "Your next client",
            service_name: evt.service_name || "",
            location: evt.location || "",
            pinged_by: evt.pinged_by || "front desk",
          }]);
          playBellChord();
          // Auto-dismiss after 12 seconds
          setTimeout(() => {
            setPings((prev) => prev.filter((p) => p.id !== id));
          }, 12000);
        } catch { /* malformed event — ignore */ }
      };
      // The backend uses the SSE message-type "checkin" for check-ins and
      // will use "provider_ping" for ping events. addEventListener with the
      // matching name is required — the default `onmessage` only fires for
      // events with no explicit type field.
      es.addEventListener("provider_ping", handlePing);
      es.onerror = () => { /* auto-reconnects */ };
      return () => {
        try { es.removeEventListener("provider_ping", handlePing); } catch { /* noop */ }
        try { es.close(); } catch { /* noop */ }
      };
    } catch { return undefined; }
  }, []);

  const dismiss = (id) => setPings((prev) => prev.filter((p) => p.id !== id));

  if (pings.length === 0) return null;

  // Only show the newest ping at any time (avoid stacking)
  const p = pings[pings.length - 1];

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
      data-testid="staff-ping-overlay"
    >
      {/* Backdrop pulse — semi-transparent amber wash */}
      <div
        className="absolute inset-0 bg-amber-500/25 backdrop-blur-[2px]"
        style={{ animation: "pingPulse 0.6s ease-out infinite alternate" }}
      />
      {/* Card */}
      <div
        className="relative pointer-events-auto rounded-3xl border-4 border-amber-400 bg-black/85 shadow-2xl px-10 py-8 text-center max-w-2xl mx-4"
        style={{ animation: "pingCardPop 0.35s cubic-bezier(.19,1.4,.35,1.02)" }}
        data-testid={`staff-ping-card-${p.id}`}
      >
        <div className="text-[10px] uppercase tracking-[0.4em] font-bold text-amber-300 mb-2">
          Client waiting for you
        </div>
        <div
          className="text-white leading-tight"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.75rem, 7vw, 4.5rem)" }}
          data-testid="staff-ping-client-name"
        >
          {p.client_name}
        </div>
        {(p.service_name || p.location) && (
          <div className="text-amber-100/85 mt-3 text-lg">
            {[p.service_name, p.location].filter(Boolean).join(" · ")}
          </div>
        )}
        <div className="text-[10px] uppercase tracking-widest text-zinc-400 mt-4">
          Pinged by {p.pinged_by}
        </div>
        <button
          onClick={() => dismiss(p.id)}
          className="mt-6 px-6 py-2.5 rounded-full text-sm font-extrabold uppercase tracking-widest text-black transition-transform hover:scale-105"
          style={{ background: "linear-gradient(135deg,#f5c265,#c58f2a)" }}
          data-testid="staff-ping-dismiss"
        >
          On my way
        </button>
      </div>
      <style>{`
        @keyframes pingPulse {
          0%   { opacity: 0.55; }
          100% { opacity: 0.15; }
        }
        @keyframes pingCardPop {
          0%   { transform: scale(0.85); opacity: 0; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
